import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Container,
  Alert,
  Modal,
  InputGroup,
} from "react-bootstrap";
import {
  Phone,
  Smartphone,
  Send,
  CheckCircle,
  Shield,
  CreditCard,
  RefreshCw,
  AlertCircle,
  Eye,
  FileText,
  DollarSign,
} from "react-feather";
import Swal from "sweetalert2";

import PPIDmtService from "../../../services/PPIDmtService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SubscriptionDialog from "../../../components/Dialogs/SubscriptionDialog";
import { useSubscriptionCheck } from "../../../hooks/useSubscriptionCheck";

const PPIMoneyTransfer = () => {
  const navigate = useNavigate();

  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [message, setMessage] = useState(null);
  const [otp, setOtp] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);

  const { premiumStatus, showDialog, setShowDialog, checkSubscriptionStatus } =
    useSubscriptionCheck();

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await checkSubscriptionStatus();
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, []);

  const generateOtpHandler = async () => {
    if (!mobile.trim() || mobile.length !== 10) {
      Swal.fire(
        "Oops!",
        "Please enter a valid 10-digit mobile number",
        "warning",
      );
      return;
    }

    if (premiumStatus !== "true") {
      setShowDialog(true);
      return;
    }

    if (loading) return;

    setLoading(true);

    try {
      const result = await PPIDmtService.generateOtp(mobile);

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setMessage(res?.data?.message || "OTP sent successfully!");
        setToken(res?.data?.token);
        setShowOtpModal(true);
        // Swal.fire({
        //   icon: "success",
        //   title: "OTP Sent!",
        //   text: "OTP has been sent to the registered mobile number",
        //   timer: 2000,
        //   showConfirmButton: false,
        // });
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyOtpHandler = async () => {
    if (!otp.trim() || otp.length !== 6) {
      Swal.fire("Oops!", "Please enter a valid 6-digit OTP", "warning");
      return;
    }

    if (loading) return;

    setLoading(true);

    try {
      const result = await PPIDmtService.verifyOtp(token, otp);

      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }

      const res = result.data;

      if (res.status === "SUCCESS") {
        setShowOtpModal(false);
        // Swal.fire({
        //   icon: "success",
        //   title: "Success!",
        //   text: result.message || "OTP verified successfully!",
        //   confirmButtonText: "OK",
        // });

        const walletAcOpened = res?.data?.wallet_ac_opened || "";

        if (walletAcOpened == true || walletAcOpened == "true") {
          navigate("/ppi-money-transfer/sender-home", {
            state: {
              customerMobile: mobile,
              data: res?.data,
            },
          });
        } else {
          navigate("/ppi-money-transfer/verify-aadhaar", {
            state: {
              customerMobile: mobile,
              data: res?.data,
            },
          });
        }

        // Reset form
        setMobile("");
        setOtp("");
        setToken(null);
        setMessage(null);
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message || "Network error",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />

        <Col md={9}>
          <Card className="shadow-sm p-4 mb-4" style={{ borderRadius: "12px" }}>
            {/* Header */}
            <div className="d-flex align-items-center mb-4">
              <div className="bg-primary p-2 rounded-circle me-3">
                <CreditCard size={24} color="white" />
              </div>
              <div>
                <h4 className="mb-1">PPI Money Transfer</h4>
                <p className="text-muted mb-0">
                  Instant money transfer using PPI wallet
                </p>
              </div>
            </div>

            {/* Info Alert */}
            {/* <Alert variant="info" className="d-flex align-items-center mb-4">
              <AlertCircle size={20} className="me-2" />
              <div className="small">
                <strong>Note:</strong> As per RBI guidelines, Money transfer is
                allowed up to ₹50,000 per month per customer using a single OTP.
              </div>
            </Alert> */}

            <Row className="g-4">
              {/* Mobile Number Input Card */}
              <Col md={6}>
                <Card className="border h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light p-2 rounded-circle me-3">
                        <Phone size={20} className="text-primary" />
                      </div>
                      <h5 className="mb-0">Customer Mobile Number</h5>
                    </div>

                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">
                        <Smartphone size={18} className="me-2 text-primary" />
                        Enter customer mobile number
                      </Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="bg-light">
                          +91
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          placeholder="Enter 10-digit mobile number"
                          value={mobile}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= 10) {
                              setMobile(value);
                            }
                          }}
                          maxLength="10"
                          disabled={loading}
                          className="py-3"
                        />
                      </InputGroup>
                    </Form.Group>

                    <Button
                      variant="primary"
                      size="lg"
                      onClick={generateOtpHandler}
                      disabled={loading || !mobile || mobile.length !== 10}
                      className="w-100 py-3"
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Sending OTP...
                        </>
                      ) : (
                        <>
                          <Send size={20} className="me-2" />
                          Send OTP
                        </>
                      )}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              {/* Features Card */}
              <Col md={6}>
                <Card className="border h-100">
                  <Card.Body className="p-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-light p-2 rounded-circle me-3">
                        <Shield size={20} className="text-primary" />
                      </div>
                      <h5 className="mb-0">Secure & Instant Transfer</h5>
                    </div>

                    <div className="mb-4">
                      <div className="d-flex align-items-start mb-3">
                        <CheckCircle
                          size={18}
                          className="text-success me-3 mt-1"
                        />
                        <div>
                          <h6 className="mb-1">Secure OTP Verification</h6>
                          <p className="text-muted small mb-0">
                            Two-factor authentication for every transaction
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-start mb-3">
                        <CheckCircle
                          size={18}
                          className="text-success me-3 mt-1"
                        />
                        <div>
                          <h6 className="mb-1">Instant Transfer</h6>
                          <p className="text-muted small mb-0">
                            Money transfers happen in real-time
                          </p>
                        </div>
                      </div>

                      <div className="d-flex align-items-start">
                        <CheckCircle
                          size={18}
                          className="text-success me-3 mt-1"
                        />
                        <div>
                          <h6 className="mb-1">24/7 Service</h6>
                          <p className="text-muted small mb-0">
                            Available round the clock for transfers
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Additional Options */}
                    <div className="mt-4 pt-3 border-top">
                      <h6 className="mb-3">Quick Actions</h6>
                      <div className="d-grid gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            navigate(
                              "/ppi-money-transfer/fund-transfer-history",
                              {},
                            );
                          }}
                        >
                          <Eye size={16} className="me-2" />
                          View Reports
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            navigate("/ppi-money-transfer/claim-refund", {});
                          }}
                        >
                          <RefreshCw size={16} className="me-2" />
                          Claim Refund
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Transaction Info */}
            <Card className="border mt-4">
              <Card.Header className="bg-light">
                <h6 className="mb-0">
                  <DollarSign size={18} className="me-2 text-primary" />
                  Transaction Information
                </h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <div className="text-center p-3">
                      <div className="bg-success bg-opacity-10 p-3 rounded-circle d-inline-flex mb-2">
                        <CreditCard size={24} className="text-info" />
                      </div>
                      <h6 className="mb-1">PPI Wallet</h6>
                      <p className="text-muted small mb-0">
                        Prepaid Instrument
                      </p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center p-3">
                      <div className="bg-success bg-opacity-10 p-3 rounded-circle d-inline-flex mb-2">
                        <Send size={24} className="text-success" />
                      </div>
                      <h6 className="mb-1">Instant Transfer</h6>
                      <p className="text-muted small mb-0">
                        Real-time processing
                      </p>
                    </div>
                  </Col>
                  <Col md={4}>
                    <div className="text-center p-3">
                      <div className="bg-info bg-opacity-10 p-3 rounded-circle d-inline-flex mb-2">
                        <Shield size={24} className="text-info" />
                      </div>
                      <h6 className="mb-1">Secure KYC</h6>
                      <p className="text-muted small mb-0">
                        Aadhaar Based e-KYC Verification
                      </p>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Card>
        </Col>
      </Row>

      {/* OTP Verification Modal */}
      <Modal show={showOtpModal} onHide={() => setShowOtpModal(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="d-flex align-items-center">
            <div className="bg-primary p-2 rounded-circle me-3">
              <Shield size={20} color="white" />
            </div>
            <span>OTP Verification</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <div className="text-center mb-4">
            <div className="bg-light p-3 rounded-circle d-inline-flex mb-3">
              <Smartphone size={40} className="text-primary" />
            </div>
            <h5>Enter OTP</h5>
            <p className="text-muted">
              Enter the 6-digit OTP sent to <strong>+91 {mobile}</strong>
            </p>
            {message && (
              <Alert variant="info" className="small">
                <CheckCircle size={16} className="me-2" />
                {message}
              </Alert>
            )}
          </div>

          <Form.Group className="mb-4">
            <Form.Label className="fw-semibold">6-Digit OTP</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter OTP"
              value={otp}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 6) {
                  setOtp(value);
                }
              }}
              maxLength="6"
              className="text-center py-3 fs-4"
              disabled={loading}
            />
            <Form.Text className="text-muted">
              OTP is valid for 5 minutes
            </Form.Text>
          </Form.Group>

          <div className="d-grid gap-2">
            <Button
              variant="primary"
              size="lg"
              onClick={verifyOtpHandler}
              disabled={loading || !otp || otp.length !== 6}
              className="py-3"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckCircle size={20} className="me-2" />
                  Verify OTP
                </>
              )}
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setShowOtpModal(false);
                setOtp("");
              }}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>

          <div className="text-center mt-4 pt-3 border-top">
            <p className="text-muted small mb-0">
              Didn't receive OTP?{" "}
              <Button
                variant="link"
                size="sm"
                className="p-0"
                onClick={generateOtpHandler}
                disabled={loading}
              >
                Resend OTP
              </Button>
            </p>
          </div>
        </Modal.Body>
      </Modal>

      <SubscriptionDialog
        show={showDialog}
        onHide={() => setShowDialog(false)}
        serviceName="PPI Money Transfer"
      />
    </Container>
  );
};

export default PPIMoneyTransfer;
